import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const AdminSidebar = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const menuItems = React.useMemo(() => ([
        { path: '/admin', icon: 'fas fa-dashboard', label: 'Dashboard' },
        { path: '/admin/destinations', icon: 'fas fa-map-marked-alt', label: 'Destinations' },
        { path: '/admin/hero-slides', icon: 'fas fa-images', label: 'Hero Slider' },
        {
            path: '/admin/packages',
            icon: 'fas fa-box',
            label: 'Packages',
            submenu: [
                { path: '/admin/packages', icon: 'fas fa-suitcase-rolling', label: 'Packages' },
                { path: '/admin/bookings', icon: 'fas fa-calendar-check', label: 'Bookings' },
                { path: '/admin/coupons', icon: 'fas fa-ticket-alt', label: 'Coupons' },
                { path: '/admin/addon-services', icon: 'fas fa-concierge-bell', label: 'Add-ons' }
            ]
        },
        // Camping Management with Submenu
        {
            path: '/admin/camping',
            icon: 'fas fa-campground',
            label: 'Camping',
            submenu: [
                { path: '/admin/camping', icon: 'fas fa-chart-pie', label: 'Dashboard' },
                { path: '/admin/camping/locations', icon: 'fas fa-map-marker-alt', label: 'Locations' },
                { path: '/admin/camping/sites', icon: 'fas fa-tree', label: 'Camping Sites' },
                { path: '/admin/camping/bookings', icon: 'fas fa-calendar-check', label: 'Bookings' }
            ]
        },
        {
            path: '/admin/messages',
            icon: 'fas fa-bullhorn',
            label: 'Communication',
            submenu: [
                { path: '/admin/messages', icon: 'fas fa-envelope', label: 'Messages' },
                { path: '/admin/newsletter', icon: 'fas fa-newspaper', label: 'Newsletter' },
                { path: '/admin/reviews', icon: 'fas fa-star', label: 'Reviews' },
                { path: '/admin/ai-chats', icon: 'fas fa-robot', label: 'AI Chats' }
            ]
        },
        { path: '/admin/wallet', icon: 'fas fa-wallet', label: 'Wallet Management' },
        { path: '/admin/users', icon: 'fas fa-users', label: 'Users' }
    ]), []);

    const [openSubmenu, setOpenSubmenu] = React.useState(null);

    const isActive = (path) => location.pathname === path;
    const isSubmenuActive = React.useCallback(
        (submenu) => submenu.some(item => location.pathname.startsWith(item.path)),
        [location.pathname]
    );

    const toggleSubmenu = (index) => {
        setOpenSubmenu(openSubmenu === index ? null : index);
    };

    React.useEffect(() => {
        const activeSubmenuIndex = menuItems.findIndex(
            (item) => item.submenu && isSubmenuActive(item.submenu)
        );

        if (activeSubmenuIndex !== -1) {
            setOpenSubmenu(activeSubmenuIndex);
        }
    }, [isSubmenuActive, menuItems]);

    // ✅ FIXED: Logout function
    const handleLogout = () => {
        // Clear all auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('adminToken');
        sessionStorage.clear();
        
        // Redirect to login
        navigate('/admin/login', { replace: true });
    };

    // Check if user is logged in
    const isLoggedIn = () => {
        return !!localStorage.getItem('token');
    };

    return (
        <div className="admin-sidebar">
            <div className="sidebar-header">
                <h2>RedGoTrip</h2>
                <p>Admin Panel</p>
                {isLoggedIn() && (
                    <span className="logged-in-badge">
                        <i className="fas fa-circle"></i> Online
                    </span>
                )}
            </div>
            
            <nav className="sidebar-nav">
                {menuItems.map((item, index) => (
                    <React.Fragment key={index}>
                        {item.submenu ? (
                            // Menu item with submenu
                            <div className="nav-section">
                                <div 
                                    className={`nav-item ${isSubmenuActive(item.submenu) ? 'active' : ''}`}
                                    onClick={() => toggleSubmenu(index)}
                                >
                                    <i className={item.icon}></i>
                                    <span>{item.label}</span>
                                    <i className={`fas fa-chevron-${openSubmenu === index ? 'down' : 'right'} submenu-arrow`}></i>
                                </div>
                                
                                {openSubmenu === index && (
                                    <div className="submenu">
                                        {item.submenu.map((subItem, subIndex) => (
                                            <Link
                                                key={subIndex}
                                                to={subItem.path}
                                                className={`submenu-item ${isActive(subItem.path) ? 'active' : ''}`}
                                            >
                                                <i className={subItem.icon}></i>
                                                <span>{subItem.label}</span>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Regular menu item
                            <Link
                                to={item.path}
                                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                            >
                                <i className={item.icon}></i>
                                <span>{item.label}</span>
                            </Link>
                        )}
                    </React.Fragment>
                ))}
            </nav>

            <div className="sidebar-footer">
                <Link to="/" className="nav-item" target="_blank">
                    <i className="fas fa-globe"></i>
                    <span>View Site</span>
                </Link>
                <button 
                    onClick={handleLogout} 
                    className="nav-item logout-btn"
                >
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                </button>
            </div>
        </div>
    );
};

export default AdminSidebar;
